import * as React from 'react';

import {List} from '@common/typescript/objects/List';

import Musthead from '@app/components/UI/MustHead/Musthead';
import FaqBlock from '@app/components/UI/FaqBlock/FaqBlock';
import RichText from '@app/components/UI/RichText/RichText';
import SloganBlockSmall from '@app/components/UI/SloganBlock/SloganBlockSmall';
import VideoEmbedSlider from '@app/components/UI/VideoEmbed/VideoEmbedSlider';
import listenerForBrowser from '@app/components/Pages/listenerForBrowser';

import {Questions} from '@app/objects/Questions';

import {rest} from '@app/components/Api';
import FaqBlockHeaderSm from '@app/components/UI/FaqBlock/FaqBlockHeaderSm';
import TabsInfoBlock from '@app/components/UI/TabsInfoBlock/TabsInfoBlock';
import withSeo from '@app/components/LocalCommon/HigherOrderComponents/WithSeo';

const CareCoordinator = () => {
	const mustHeadText = 'Turn your passion for pets into a career by helping families navigate one of the hardest moments of loving a pet.';
	const coordinatorRichText = 'At Lap of Love, we believe in connecting with people and helping pets find comfort and peace in their end-of-life journey. We also believe that assisting a family through the passing of their pet is one of the most rewarding aspects of this profession. Lap of Love is seeking empathetic, self-motivated individuals with excellent communication skills and the desire to assist pet families. We are dedicated to compassionate end-of-life care with a sole focus on veterinary in-home hospice and euthanasia.';

	const videoSrc = [
		'https://www.youtube.com/embed/r0Hcfq0N8qQ?showinfo=0',
		'https://www.youtube.com/embed/5Ls0tiM5BLI?showinfo=0',
		'https://www.youtube.com/embed/LYFDx0DIcqQ?showinfo=0'
	];
	const navItems = [
		{name: 'responsibilities',
			title: 'Responsibilities',
			issues: [{title: 'Answers client calls in a timely and supportive manner'},
				{title: 'Addresses client concerns with compassion and coordinates care'},
				{title: 'Gathers and accurately documents the client’s personal and pet related medical information'},
				{title: 'Possesses the ability to schedule appointments in a logical and effective manner'},
				{title: 'Manages and supports client relations for multiple Lap of Love veterinarians throughout the country'},
				{title: 'Goes above and beyond to assist our clients, their pets, and our doctors'},
				{title: 'Other duties as may be assigned and may arise from time to time'},
				{title: 'Communicates routinely with veterinarians and staff, keeping them informed of client requests, appointment information, and any other pertinent information'}
			]},

		{name: 'skills', title: 'Required Skills', issues: [
				{title: 'Strong intrinsic motivation to do profound work'},
				{title: 'Strong and professional interpersonal skills'},
				{title: 'Excellent communication skills'},
				{title: 'Exercises critical thinking skills'},
				{title: 'Strong proficiency in computer literacy, navigation, and troubleshooting'},
				{title: 'Displays compassion, control, and confidence in all aspects of the position'},
				{title: 'Ability to multi-task using multiple computer tabs/platforms while engaging in conversation'},
				{title: 'High attention to detail and accuracy'},
				{title: 'Must be comfortable utilizing the internet and programs, such as, Gmail, Google Calendar and web-based communication platforms. Must have the ability to quickly learn custom software'}
		]},
		{name: 'schedule',
			title: 'Schedule',
			issues: [
				{title: 'Support Center hours are 7am to 11pm, 7 days a week, 365 days a year'},
				{title: 'May include nights/weekends'},
				{title: 'Must have ability to work holidays Preferred'}]},
		{name: 'experience', title: 'Preferred Experience',
			issues: [
				{title: 'Previous experience practicing front-line animal care'},
				{title: 'Customer Service: 2 years'}]}
	];

	const videoText = 'Care coordinators are special people. They not only have a sincere passion for pets but also possess an incredible amount of care and love for humans. This appreciation for the human-animal bond is what makes Lap of Love care coordinators unique, and the work they do so powerful.';

	const videoTitle = 'A few care coordinators share their love for the job.';

	const externalResourceLink = 'https://form.jotform.com/10711623150';

	const [questions, setQuestions] = React.useState<Questions[]>([]);
	const [currentIndex, setCurrentIndex] = React.useState(0);
	const [currentIssue, setCurrentIssue] = React.useState(navItems[currentIndex].issues);
	const [navCollapse, setNavCollapse] = React.useState(false);

	const onSetIssue = (index) => {
		setCurrentIndex(index);
		setCurrentIssue(navItems[index].issues);
	};

	const openInNewTab = (arg) => {
		const win = window.open(arg, '_blank');
		win && win.focus();
	};

	React.useEffect(() => {
		rest.get<List<Questions>>('v1/faq/careCoordinatorPage').then(res => setQuestions(res.list));
		const mql = window.matchMedia('screen and (max-width: 768px)');

		const listenerFunc = () => {
			if (mql.matches) {
				setNavCollapse(true);
			} else {
				setNavCollapse(false);
			}

		};
		listenerFunc();
		listenerForBrowser(mql, listenerFunc);
	}, []);
	return(
		<div className="care-coordinator">
			<Musthead
				title="Become a Care Coordinator"
				imageSrc={require('@app/content/Lap-of-love-veterinary-care-coordinator-1.jpg')}
				text={mustHeadText}
				buttonText="Apply now"
				btnPath={externalResourceLink}
				onBtnClick={openInNewTab}
				imageAlt="Lap of love jobs"
			/>
			<RichText
				title="What it’s like being a Lap of Love care coordinator"
				text={coordinatorRichText}
			/>
			<SloganBlockSmall
				title="Ready to make a lasting impact on pets and their families?"
				btnText={<a href={externalResourceLink} target="_blank" className="btn btn-primary">Apply now</a>}
			/>
			<VideoEmbedSlider
				videoText={videoText}
				videoSrc={videoSrc}
				videoTitle={videoTitle}
			/>
			<div className="care-coordinator__faq">
				<div className="care-coordinator__faq-content">
					<FaqBlockHeaderSm
						title="You might find yourself asking..."
						btnText="View all FAQ"
						clickUrl="/frequently-asked-questions"
					/>
					<FaqBlock questions={questions} />
				</div>
			</div>
			<TabsInfoBlock
				navItems={navItems}
				currentIndex={currentIndex}
				onSetIssue={onSetIssue}
				navCollapse={navCollapse}
				currentIssue={currentIssue}
			/>
		</div>
	);
};

export default withSeo(CareCoordinator, state => state.careCoordinatorPage, 'careCoordinatorPage', 'careers/become-a-care-coordinator');